import { useImperativeHandle, useRef } from "react";

import CompositeColorPicker from "../CompositeColorPicker/CompositeColorPicker";
import {
  Button,
  Card,
  Checkbox,
  ColorTrigger,
  Flex,
  Input,
  isNullish,
  Popover,
  Text
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";

/*
 * type Value = {
 *   fFlagColor1: Nullable<HexColor>,
 *   fFlagColor2: Nullable<HexColor>,
 *   fFlagColor3: Nullable<HexColor>,
 *   fFlagLabel: Nullable<string>,
 *   fTriPosition: 0 | 1
 * }
 *
 * type Props = {
 *   openCtrlRef?: MutableRefObject<{ open: boolean; setOpen: () => void }>
 *   disabled?: boolean
 *   onChange: (value: Value) => void
 *   value: Value
 * }
 */
export const BoxFlagControl = props => {
  const popoverOpenCtrlRef = useRef(null);
  useImperativeHandle(props.openCtrlRef, () => popoverOpenCtrlRef.current);

  const triggerTestId = props.dataTestId ? `${props.dataTestId}-btn` : props.triggerTestId;
  const popoverTestId = props.dataTestId ? `${props.dataTestId}-popover` : props.popoverTestId;

  const color1OpenCtrlRef = useRef(null);
  const color3OpenCtrlRef = useRef(null);
  const colorEnabled = !isNullish(props.value.fFlagColor1);

  const handleSetColorToggle = () => {
    const nextEnabled = !colorEnabled;
    const nextValue = { ...props.value };

    if (nextEnabled) {
      nextValue.fFlagColor1 = "#FF0000";
    } else {
      nextValue.fFlagColor1 = null;
    }

    nextValue.fFlagColor3 = null;
    props.onChange(nextValue);
    color1OpenCtrlRef.current?.setOpen(nextEnabled);
  };

  const handleColor1Change = selectedColor => {
    const nextValue = { ...props.value };
    nextValue.fFlagColor1 = selectedColor;
    props.onChange(nextValue);
  };

  const handleColor2Change = selectedColor => {
    const nextValue = { ...props.value };
    nextValue.fFlagColor2 = selectedColor;
    props.onChange(nextValue);
  };

  const color2OpenCtrlRef = useRef(null);
  const dashedEnabled = !isNullish(props.value.fFlagColor2);

  const handleSetDashedToggle = () => {
    const nextEnabled = !dashedEnabled;
    const nextValue = { ...props.value };

    if (nextEnabled) {
      nextValue.fFlagColor2 = "#000000";
    } else {
      nextValue.fFlagColor2 = null;
    }

    props.onChange(nextValue);
    color2OpenCtrlRef.current?.setOpen(nextEnabled);
  };

  const handleColor3Change = selectedColor => {
    const nextValue = { ...props.value };
    nextValue.fFlagColor3 = selectedColor;
    props.onChange(nextValue);
  };

  const triLabel = props.value.fFlagLabel || "";
  const triLabelEnabled = !isNullish(props.value.fFlagLabel);

  const handleSetLabelToggle = () => {
    const nextEnabled = !triLabelEnabled;
    const nextValue = { ...props.value };

    if (nextEnabled) {
      nextValue.fFlagLabel = "";
    } else {
      nextValue.fFlagLabel = null;
    }

    props.onChange(nextValue);
  };

  const handleLabelChange = e => {
    const { value } = e.target;
    const nextValue = { ...props.value };
    nextValue.fFlagLabel = value.substring(0, 1);
    props.onChange(nextValue);
  };

  const triPositionEnabled = props.value.fTriPosition === 1;
  const handlePositionToggle = () => {
    const nextEnabled = !triPositionEnabled;
    const nextValue = { ...props.value };
    nextValue.fTriPosition = nextEnabled ? 1 : 0;
    props.onChange(nextValue);
  };

  const handleOK = () => {
    popoverOpenCtrlRef.current?.setOpen(false);
  };

  return (
    <Popover
      openCtrlRef={popoverOpenCtrlRef}
      arrowOptions={false}
      enabled={!props.disabled}
      preventTree
      floatingProps={{ "data-testid": popoverTestId }}
      reference={
        <ColorTrigger
          disabled={props.disabled}
          color={props.value.fFlagColor1}
          size="medium"
          iconName="flag-pennant"
          {...props.referenceProps}
          data-testid={triggerTestId}
        />
      }
    >
      <Card>
        <Flex direction="column" gap="12px" width="300px">
          <Text modifier="medium" size="small">
            {appstr("Box Flag")}
          </Text>
          <Flex justifyContent="space-between" alignItems="center">
            <Checkbox
              id="toggleFlagProperties-color"
              label={appstr("setcolor")}
              size="medium"
              checked={colorEnabled}
              onToggle={handleSetColorToggle}
            />
            <Flex gap={"12px"}>
              <CompositeColorPicker
                enabled={colorEnabled}
                openCtrlRef={color1OpenCtrlRef}
                trigger={
                  <ColorTrigger iconName="box-flag-fill" size={"medium"} disabled={!colorEnabled} />
                }
                onChange={handleColor1Change}
                currentColor={props.value.fFlagColor1}
              />
              <CompositeColorPicker
                enabled={colorEnabled}
                openCtrlRef={color3OpenCtrlRef}
                trigger={
                  <ColorTrigger
                    iconName="box-flag-fill-alt"
                    size={"medium"}
                    disabled={!colorEnabled}
                  />
                }
                onChange={handleColor3Change}
                currentColor={props.value.fFlagColor3}
              />
            </Flex>
          </Flex>
          <Flex justifyContent="space-between" alignItems="center">
            <Checkbox
              id="toggleFlagProperties-dash"
              label={appstr("setdashed")}
              size="medium"
              checked={dashedEnabled}
              onToggle={handleSetDashedToggle}
            />
            <CompositeColorPicker
              enabled={dashedEnabled}
              openCtrlRef={color2OpenCtrlRef}
              trigger={<ColorTrigger iconName="pen" size={"medium"} disabled={!dashedEnabled} />}
              onChange={handleColor2Change}
              currentColor={props.value.fFlagColor2}
            />
          </Flex>
          <Flex alignItems="center">
            <Checkbox
              id="toggleFlagProperties-label"
              label={appstr("setlabel")}
              size="medium"
              checked={triLabelEnabled}
              onToggle={handleSetLabelToggle}
            />
            <Flex height="26px" />
          </Flex>
          {triLabelEnabled ? (
            <Flex>
              <Input
                autoFocus
                inputOnly
                maxLength={1}
                disabled={!triLabelEnabled}
                size="medium"
                value={triLabel}
                onChange={handleLabelChange}
              />
            </Flex>
          ) : null}
          <Flex justifyContent="space-between" alignItems="center">
            <Checkbox
              id="toggleFlagProperties-position"
              label={appstr("setposition")}
              size="medium"
              checked={triPositionEnabled}
              onToggle={handlePositionToggle}
            />
            <Flex height="26px" />
          </Flex>
          <Flex justifyContent="flex-end">
            <Button modifier={"secondary"} size={"small"} onClick={handleOK}>
              {appstr("OK")}
            </Button>
          </Flex>
        </Flex>
      </Card>
    </Popover>
  );
};
